/**
 * Turn a link somebody typed into one that is safe to put in an `href`.
 *
 * A URL written without a scheme — `linkedin.com/in/someone`, which is what a business
 * card, an email signature and a half-copied address bar all produce — is a *relative*
 * reference in HTML, not a shorthand for `https://`. The browser resolves it against the
 * page it is on, so a team card on `/team` sent visitors to
 * `/team/linkedin.com/in/someone`, which is a 404 on our own site. The link looked
 * completely ordinary in the admin and was broken for every visitor who clicked it.
 *
 * The repair belongs here, at the point of use, rather than in the settings schema:
 * `socialSettingSchema` stores `z.string()` on purpose, so there are already rows in the
 * database written the shorthand way. Normalising on render fixes those *and* the next
 * one somebody types, without a data migration that would have to be repeated the first
 * time an admin pastes an address the same way again.
 *
 * @returns the address to link to, or `null` when there is nothing safe to link to — so
 * callers use it as the same condition that decides whether to render the icon at all.
 */

/**
 * Schemes we will emit as-is.
 *
 * `tel:` and `mailto:` are here because this helper is the obvious place for the next
 * contact link, not because a LinkedIn field should hold one.
 */
const SAFE_SCHEME = /^(?:https?:|mailto:|tel:)/i;

/**
 * Anything shaped like `scheme:`.
 *
 * Used to tell "the author wrote a scheme we do not allow" from "the author wrote no
 * scheme at all". The two need opposite treatment and the difference is not otherwise
 * visible: `javascript:alert(1)` and `linkedin.com/in/x` are both just strings here.
 */
const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

export function externalHref(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (SAFE_SCHEME.test(trimmed)) return trimmed;

  /*
    A scheme we do not allow is refused outright rather than prefixed.

    Prefixing would turn `javascript:alert(document.cookie)` into
    `https://javascript:alert(...)`, which is merely broken — but these values come from
    an admin-editable field that renders into an `href`, and the honest answer to an
    unrecognised scheme is to not link at all. Returning `null` means the caller drops
    the icon instead of showing one that cannot work.
  */
  if (ANY_SCHEME.test(trimmed)) return null;

  // Protocol-relative (`//linkedin.com/in/x`). The site is https-only, so there is no
  // case where inheriting the page's scheme is more correct than stating it.
  if (trimmed.startsWith('//')) return `https:${trimmed}`;

  return `https://${trimmed}`;
}
